document.documentElement.classList.remove('no-js');

(() => {
  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => [...root.querySelectorAll(selector)];

  function initMobileMenu() {
    const menu = qs('[data-mobile-menu]');
    const open = qs('[data-menu-open]');
    if (!menu || !open) return;
    const closeButtons = qsa('[data-menu-close]', menu);

    const setOpen = (isOpen) => {
      menu.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      open.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      document.body.classList.toggle('menu-open', isOpen);
      if (isOpen) qs('a, button', menu)?.focus();
    };

    open.addEventListener('click', () => setOpen(true));
    closeButtons.forEach((button) => button.addEventListener('click', () => setOpen(false)));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && menu.getAttribute('aria-hidden') === 'false') setOpen(false);
    });
  }

  function initCarousels() {
    qsa('[data-carousel]').forEach((carousel) => {
      const track = qs('[data-carousel-track]', carousel);
      if (!track) return;
      const prev = qs('[data-carousel-prev]', carousel);
      const next = qs('[data-carousel-next]', carousel);

      const amount = () => {
        const first = track.firstElementChild;
        if (!first) return 360;
        return first.getBoundingClientRect().width + 16;
      };
      prev?.addEventListener('click', () => track.scrollBy({ left: -amount(), behavior: 'smooth' }));
      next?.addEventListener('click', () => track.scrollBy({ left: amount(), behavior: 'smooth' }));
    });
  }

  function initProductGalleries(root = document) {
    qsa('[data-product-gallery]', root).forEach((gallery) => {
      if (gallery.dataset.galleryInitialized === 'true') return;

      gallery.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-gallery-thumb]');
        if (!thumb || !gallery.contains(thumb)) return;

        const main = qs('[data-gallery-main]', gallery);
        const src = thumb.dataset.src;
        const srcset = thumb.dataset.srcset || '';
        const alt = thumb.dataset.alt || '';
        if (!main || main.tagName !== 'IMG' || !src) return;

        event.preventDefault();

        // Shopify product images use srcset. Updating only src can leave the
        // old responsive image visible, so update both attributes together.
        if (srcset) {
          main.setAttribute('srcset', srcset);
        } else {
          main.removeAttribute('srcset');
        }
        main.setAttribute('src', src);
        main.setAttribute('alt', alt);

        qsa('[data-gallery-thumb]', gallery).forEach((item) => {
          item.setAttribute('aria-current', 'false');
        });
        thumb.setAttribute('aria-current', 'true');
      });

      gallery.dataset.galleryInitialized = 'true';
    });
  }

  function formatMoney(cents, currency = 'USD') {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency
      }).format(cents / 100);
    } catch (_) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  function initProductForms() {
    qsa('[data-product-form-wrap]').forEach((wrap) => {
      const select = qs('[data-variant-select]', wrap);
      const price = qs('[data-product-price]', wrap);
      const compare = qs('[data-product-compare]', wrap);
      const button = qs('[data-add-to-cart]', wrap);
      const buttonLabel = qs('[data-add-to-cart-label]', wrap);
      const stickyPrice = qs('[data-sticky-price]');
      const variantsNode = qs('[data-variants-json]', wrap);
      if (!select || !variantsNode) return;

      let variants = [];
      try { variants = JSON.parse(variantsNode.textContent); } catch (_) { return; }
      const currency = wrap.dataset.currency || 'USD';

      const update = () => {
        const id = String(select.value);
        const variant = variants.find((item) => String(item.id) === id);
        if (!variant) return;

        if (price) price.textContent = formatMoney(variant.price, currency);
        if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price, currency);

        if (compare) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            compare.hidden = false;
            compare.textContent = formatMoney(variant.compare_at_price, currency);
          } else {
            compare.hidden = true;
          }
        }

        if (button) {
          button.disabled = !variant.available;
          if (buttonLabel) buttonLabel.textContent = variant.available ? 'Add to cart' : 'Sold out';
        }

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      };

      select.addEventListener('change', update);
      update();
    });
  }

  function initStickyAddToCart() {
    const sticky = qs('[data-sticky-atc]');
    const mainButton = qs('[data-main-atc]');
    if (!sticky || !mainButton || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      sticky.classList.toggle('is-visible', !entries[0].isIntersecting);
    }, { threshold: 0 });
    observer.observe(mainButton);

    qs('[data-sticky-atc-button]', sticky)?.addEventListener('click', () => mainButton.click());
  }

  function initQuiz(root = document) {
    const quiz = qs('[data-quiz]', root);
    if (!quiz || quiz.dataset.quizInitialized === 'true') return;
    quiz.dataset.quizInitialized = 'true';

    const steps = qsa('[data-quiz-step]', quiz);
    const progress = qs('[data-quiz-progress]', quiz);
    const gate = qs('[data-quiz-gate]', quiz);
    const resultStep = qs('[data-quiz-result]', quiz);
    const title = qs('[data-result-title]', quiz);
    const copy = qs('[data-result-copy]', quiz);
    const resultEmail = qs('[data-result-email]', quiz);
    const tagField = qs('[data-result-tag]', quiz);
    const emailForm = qs('[data-quiz-email-form]', quiz);
    const emailInput = qs('[data-quiz-email]', quiz);
    const firstNameInput = qs('[data-quiz-first-name]', quiz);
    const emailButton = qs('[data-quiz-email-submit]', quiz);
    const emailButtonLabel = qs('[data-quiz-email-button-label]', quiz);
    const emailStatus = qs('[data-quiz-email-status]', quiz);
    const publicKey = (quiz.dataset.klaviyoPublicKey || '').trim();
    const listId = (quiz.dataset.klaviyoListId || '').trim();
    const revision = quiz.dataset.klaviyoRevision || '2025-07-15';
    const storageKey = `jr-focus-quiz:${window.location.pathname}`;
    let index = 0;
    let resultCode = '';
    const answers = {};

    const resultContent = {
      fog: {
        profile: 'The Foggy Starter',
        title: 'Your focus type: The Foggy Starter',
        copy: 'Your biggest opportunity is building a consistent start that supports mental clarity before distractions stack up.'
      },
      energy: {
        profile: 'The Afternoon Fader',
        title: 'Your focus type: The Afternoon Fader',
        copy: 'Your energy tends to set the pace, especially when the afternoon dip makes sustained focus harder.'
      },
      focus: {
        profile: 'The Tab Hopper',
        title: 'Your focus type: The Tab Hopper',
        copy: 'You have plenty to do, but task-switching makes it difficult to lock into your most important work.'
      },
      stress: {
        profile: 'The Overloaded Achiever',
        title: 'Your focus type: The Overloaded Achiever',
        copy: 'You push hard, but mental overload can make it difficult to protect your attention and stay consistent.'
      }
    };

    const saveState = (email = '') => {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify({ answers, resultCode, email }));
      } catch (_) {}
    };

    const restoreState = () => {
      try {
        const saved = JSON.parse(sessionStorage.getItem(storageKey) || '{}');
        if (saved.answers && typeof saved.answers === 'object') Object.assign(answers, saved.answers);
        if (saved.resultCode && resultContent[saved.resultCode]) resultCode = saved.resultCode;
        return saved;
      } catch (_) {
        return {};
      }
    };

    const show = (nextIndex) => {
      index = Math.max(0, Math.min(nextIndex, steps.length - 1));
      steps.forEach((step, stepIndex) => { step.hidden = stepIndex !== index; });
      if (gate) gate.hidden = true;
      if (resultStep) resultStep.hidden = true;
      if (progress) progress.style.width = `${((index + 1) / steps.length) * 100}%`;
      const back = qs('[data-quiz-back]', steps[index]);
      if (back) back.hidden = index === 0;
    };

    const calculateResult = () => {
      const scores = { fog: 0, energy: 0, focus: 0, stress: 0 };
      Object.entries(answers).forEach(([key, value]) => {
        if (Object.prototype.hasOwnProperty.call(scores, value)) {
          scores[value] += key === 'challenge' ? 2 : 1;
        }
      });
      resultCode = Object.entries(scores).sort((a, b) => b[1] - a[1])[0][0];
      return resultCode;
    };

    const setTagField = () => {
      if (tagField && resultCode) {
        tagField.value = `newsletter,prelaunch,focus-quiz,quiz-${resultCode}`;
      }
    };

    const showGate = () => {
      calculateResult();
      setTagField();
      saveState();
      steps.forEach((step) => { step.hidden = true; });
      if (resultStep) resultStep.hidden = true;
      if (gate) gate.hidden = false;
      if (progress) progress.style.width = '100%';
      gate?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.setTimeout(() => emailInput?.focus(), 350);
    };

    const showResult = (email = '') => {
      if (!resultCode || !resultContent[resultCode]) calculateResult();
      const content = resultContent[resultCode];
      if (!content) return;

      if (title) title.textContent = content.title;
      if (copy) copy.textContent = content.copy;
      if (resultEmail) resultEmail.textContent = email || 'your inbox';

      steps.forEach((step) => { step.hidden = true; });
      if (gate) gate.hidden = true;
      if (resultStep) resultStep.hidden = false;
      if (progress) progress.style.width = '100%';
      saveState(email);
      resultStep?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const setSubmitting = (isSubmitting) => {
      if (emailButton) emailButton.disabled = isSubmitting;
      if (emailButtonLabel) emailButtonLabel.textContent = isSubmitting ? 'Sending Your Profile…' : 'Send My Focus Type';
    };

    const submitToKlaviyo = async (email, firstName) => {
      const content = resultContent[resultCode];
      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Jackrabbit Shopify Focus Quiz',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email,
                  ...(firstName ? { first_name: firstName } : {}),
                  properties: {
                    'Jackrabbit Focus Type': content.profile,
                    'Jackrabbit Focus Code': resultCode,
                    'Jackrabbit Quiz Challenge': answers.challenge || '',
                    'Jackrabbit Quiz Timing': answers.timing || '',
                    'Jackrabbit Quiz Routine': answers.routine || '',
                    'Jackrabbit Quiz Goal': answers.goal || '',
                    'Jackrabbit Quiz Format': answers.format || '',
                    'Jackrabbit Quiz Source': 'Shopify Focus Quiz',
                    'Jackrabbit Quiz Completed At': new Date().toISOString()
                  }
                }
              }
            }
          },
          relationships: {
            list: {
              data: {
                type: 'list',
                id: listId
              }
            }
          }
        }
      };

      const response = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(publicKey)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          'Accept': 'application/vnd.api+json',
          'revision': revision
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        let detail = 'We could not send your profile. Please try again.';
        try {
          const body = await response.json();
          detail = body?.errors?.[0]?.detail || detail;
        } catch (_) {}
        throw new Error(detail);
      }
    };

    qsa('[data-quiz-option]', quiz).forEach((option) => {
      option.addEventListener('click', () => {
        const step = option.closest('[data-quiz-step]');
        const key = step?.dataset.key;
        const value = option.dataset.value;
        if (!key || !value) return;
        answers[key] = value;
        qsa('[data-quiz-option]', step).forEach((item) => item.classList.remove('is-selected'));
        option.classList.add('is-selected');
        qs('[data-quiz-next]', step)?.removeAttribute('disabled');
        qs('[data-quiz-submit]', step)?.removeAttribute('disabled');
      });
    });

    qsa('[data-quiz-next]', quiz).forEach((button) => {
      button.addEventListener('click', () => {
        if (index < steps.length - 1) show(index + 1);
      });
    });
    qsa('[data-quiz-back]', quiz).forEach((button) => {
      button.addEventListener('click', () => show(index - 1));
    });
    qs('[data-quiz-submit]', quiz)?.addEventListener('click', showGate);

    const handleEmailSubmit = async (event) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      event?.stopImmediatePropagation?.();

      if (!emailInput?.checkValidity()) {
        emailInput?.reportValidity();
        return false;
      }

      const email = emailInput.value.trim();
      const firstName = firstNameInput?.value.trim() || '';
      calculateResult();
      setTagField();
      saveState(email);

      if (!publicKey || !listId) {
        if (emailStatus) {
          emailStatus.textContent = 'Quiz setup is incomplete. Add both the Klaviyo Public Site ID and main List ID in this Focus quiz section, save, and try again.';
        }
        return false;
      }

      if (emailStatus) emailStatus.textContent = '';
      setSubmitting(true);
      try {
        await submitToKlaviyo(email, firstName);
        if (emailStatus) emailStatus.textContent = 'Success — your focus type is on the way.';
        showResult(email);
      } catch (error) {
        console.error('Jackrabbit Klaviyo quiz submission failed:', error);
        if (emailStatus) {
          emailStatus.textContent = error?.message || 'We could not send your result. Please check the email and try again.';
        }
      } finally {
        setSubmitting(false);
      }
      return false;
    };

    // The email gate is intentionally not a Shopify customer form. Using a
    // button click prevents any native form post or third-party form script
    // from refreshing the page and returning the visitor to question one.
    emailButton?.addEventListener('click', handleEmailSubmit);
    emailInput?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') handleEmailSubmit(event);
    });

    const restored = restoreState();
    const params = new URLSearchParams(window.location.search);
    if (params.get('customer_posted') === 'true' && resultCode) {
      showResult(restored.email || 'your inbox');
    } else {
      show(0);
    }
  }

  function initQuantityUpdates() {
    qsa('[data-cart-quantity]').forEach((input) => {
      input.addEventListener('change', () => {
        const form = input.closest('form');
        form?.submit();
      });
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initMobileMenu();
    initCarousels();
    initProductGalleries();
    initProductForms();
    initStickyAddToCart();
    initQuiz();
    initQuantityUpdates();
  });

  document.addEventListener('shopify:section:load', (event) => {
    initProductGalleries(event.target);
    initQuiz(event.target);
  });
})();
